import { alphaSort, alphaSorter } from 'vet-bones/utils';

import { ProjectPropertyType } from 'src/constants/project';
import { ModelConceptAttribute } from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import {
  OWL_DATATYPE_PROPERTY_IRI,
  RDFS_COMMENT_IRI,
  RDFS_LABEL_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { getAbbreviationOrFullIri } from 'src/utils/getAbbreviationOrFullIri';
import { getInheritedAttributesByClass } from 'src/utils/getInheritedAttributesByClass';
import { getPropertyIrisForModelConcept } from 'src/utils/stringify/getPropertyIrisForModelConcept';

const modelConceptAttributesSort = alphaSort((item: [string, any]) => item[0]);

export const stringifyModelConceptAttributes = (
  model: ModelSnapshot,
  projectPropertyType: ProjectPropertyType,
  namespaces: Record<string, string>,
  filter?: (attribute: ModelConceptAttribute) => boolean,
  classIris?: string[]
): string => {
  const abbreviate = (iri: string) => getAbbreviationOrFullIri(iri, namespaces);

  const attributesByIri = { ...model.modelData.attributesByIri };

  if (classIris) {
    const inheritedAttributesByClass = getInheritedAttributesByClass(
      model,
      classIris
    );

    Object.keys(inheritedAttributesByClass).forEach((classIri) => {
      Object.keys(inheritedAttributesByClass[classIri]).forEach(
        (attributeKey) => {
          attributesByIri[attributeKey] = {
            ...attributesByIri[attributeKey],
            domains: {
              ...attributesByIri[attributeKey].domains,
              [classIri]: { value: classIri, models: [] },
            },
          };
        }
      );
    });
  }

  let attributes = Object.entries(attributesByIri);
  if (filter) {
    attributes = attributes.filter(([, attribute]) => filter(attribute));
  }

  return attributes
    .sort(modelConceptAttributesSort)
    .map(([value, { domains, ranges }]) => {
      const abbreviated = abbreviate(value);

      const lines = [
        `${abbreviated} a ${abbreviate(OWL_DATATYPE_PROPERTY_IRI)}`,
      ];

      const label = model.modelData.labelsByIri[value];
      if (label) {
        lines.push(
          `${abbreviate(RDFS_LABEL_IRI)} "${escapeDoubleQuotes(label)}"`
        );
      }

      const description = model.modelData.descriptionsByIri[value];
      if (description) {
        lines.push(
          `${abbreviate(RDFS_COMMENT_IRI)} "${escapeDoubleQuotes(description)}"`
        );
      }

      const { domainIri, rangeIri } = getPropertyIrisForModelConcept(
        projectPropertyType,
        model,
        value
      );

      const domainIris = Object.keys(domains);
      if (domainIris.length) {
        const objects = domainIris
          .map((object) => abbreviate(object))
          .sort(alphaSorter)
          .join(', ');
        lines.push(`${abbreviate(domainIri)} ${objects}`);
      }

      const rangeIris = Object.keys(ranges);
      if (rangeIris.length) {
        const objects = rangeIris
          .map((object) => abbreviate(object))
          .sort(alphaSorter)
          .join(', ');
        lines.push(`${abbreviate(rangeIri)} ${objects}`);
      }

      return `${lines.join(' ;\n  ')} .`;
    })
    .join('\n');
};
